import { englishError } from "../utils/databaseErrors";
// src/components/PaymentTable.jsx
import React, { useEffect, useState, useMemo, useRef } from "react";
import { supabase } from "../supabaseClient";
import "./PaymentTable.css";

/* ==== ثوابت ودوال مساعدة ==== */
const METHOD_LABELS = {
  cash: "Cash",
  vodafone: "Vodafone Cash",
  insta: "InstaPay",
  cheque: "Cheque",
  bank: "Bank transfer",
};

function pad(n) {
  return String(n).padStart(2, "0");
}
function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
function addDays(dateStr, delta) {
  const d = new Date(dateStr);
  d.setDate(d.getDate() + delta);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
function startOfDayISO(dateStr) {
  return new Date(`${dateStr}T00:00:00.000`).toISOString();
}
function endOfDayISO(dateStr) {
  return new Date(`${dateStr}T23:59:59.999`).toISOString();
}

/* ================== المكون الرئيسي ================== */
function PaymentTable({ user }) {
  const showroomId = Number(user?.showroom_id) || null;
  const isAdmin =
    String(user?.role || "").toLowerCase() === "admin" ||
    String(user?.role || "").toLowerCase() === "owner";

  const [customers, setCustomers] = useState([]);
  const [showrooms, setShowrooms] = useState([]);
  const showroomNameById = useMemo(
    () => Object.fromEntries(showrooms.map((s) => [Number(s.id), s.name])),
    [showrooms]
  );

  // دفعات المعرض الحالي
  const [payments, setPayments] = useState([]);
  // دفعات باقي المعارض (لما نعرضها)
  const [othersByShowroom, setOthersByShowroom] = useState({});

  // تفاصيل المبيعات (للاستخدام العام في الجدول)
  const [salesDetails, setSalesDetails] = useState([]);

  // فلاتر التاريخ
  const [dateFrom, setDateFrom] = useState(todayISO());
  const [dateTo, setDateTo] = useState(todayISO());

  // إظهار باقي المعارض
  const [showOthers, setShowOthers] = useState(false);
  const [loadingOthers, setLoadingOthers] = useState(false);

  // فورم إضافة دفعة
  const [showAddPayment, setShowAddPayment] = useState(false);
  const [savingPayment, setSavingPayment] = useState(false);
  const savingPaymentRef = useRef(false);
  const [form, setForm] = useState({
    customer_id: "",
    amount: "",
    payment_date: todayISO(),
    notes: "",
    reference: "",
    method: "cash",
    voucher_no: "",
  });

  // حالة التعديل
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({
    customer_id: "",
    amount: "",
    payment_date: "",
    notes: "",
    reference: "",
    method: "cash",
    voucher_no: "",
  });

  // ===== شريط الرصيد الحقيقي للعميل المختار (بعد الخصم، كل المعارض، كل الوقت) =====
  const [bannerBalance, setBannerBalance] = useState(0);
  const [bannerLoading, setBannerLoading] = useState(false);

  const selectedCustomerId = Number(form.customer_id);
  const selectedCustomer = customers.find((c) => c.id === selectedCustomerId);

  // تحميل العملاء والمعارض مرة واحدة
  useEffect(() => {
    (async () => {
      const [{ data: cust }, { data: srs }] = await Promise.all([
        supabase.from("customer").select("id, name"),
        supabase.from("showroom").select("id, name"),
      ]);
      setCustomers(cust || []);
      setShowrooms(srs || []);
    })();
  }, []);

  // تحميل دفعات المعرض الحالي ضمن الفترة
  useEffect(() => {
    if (!showroomId) return;
    fetchOwnShowroomPayments();
    // eslint-disable-next-line
  }, [showroomId, dateFrom, dateTo]);

  // لما العميل المختار يتغير، أعد حساب الرصيد الحقيقي
  useEffect(() => {
    if (selectedCustomerId) {
      recomputeBannerBalance(selectedCustomerId);
    } else {
      setBannerBalance(0);
    }
    // eslint-disable-next-line
  }, [selectedCustomerId]);

  async function fetchOwnShowroomPayments() {
    const [{ data: payData, error: payErr }, { data: salesDetData, error: salesErr }] =
      await Promise.all([
        supabase
          .from("payment")
          .select(
            "id, customer_id, amount, payment_date, notes, reference, method, voucher_no, showroom_id"
          )
          .eq("showroom_id", showroomId)
          .gte("payment_date", startOfDayISO(dateFrom))
          .lte("payment_date", endOfDayISO(dateTo))
          .order("payment_date", { ascending: false }),
        supabase
          .from("sale_details")
          .select("sale_id, total_price, sale:sale_id(id, customer_id)"),
      ]);

    if (payErr) console.error("Error fetching payments:", payErr);
    if (salesErr) console.error("Error fetching sales details:", salesErr);

    setPayments(payData || []);
    setSalesDetails(salesDetData || []);
  }

  // عرض دفعات باقي المعارض
  async function fetchOthersShowrooms() {
    setLoadingOthers(true);
    try {
      const { data: payData, error } = await supabase
        .from("payment")
        .select(
          "id, customer_id, amount, payment_date, notes, reference, method, voucher_no, showroom_id"
        )
        .neq("showroom_id", showroomId)
        .gte("payment_date", startOfDayISO(dateFrom))
        .lte("payment_date", endOfDayISO(dateTo))
        .order("showroom_id", { ascending: true })
        .order("payment_date", { ascending: false });

      if (error) {
        console.error("Error fetching other showrooms payments:", error);
        alert("Unable to load payments from other showrooms");
        setOthersByShowroom({});
        return;
      }

      const grouped = {};
      (payData || []).forEach((p) => {
        const sid = Number(p.showroom_id) || 0;
        if (!grouped[sid]) grouped[sid] = [];
        grouped[sid].push(p);
      });
      setOthersByShowroom(grouped);
    } finally {
      setLoadingOthers(false);
    }
  }

  // ===== حساب "الرصيد الحقيقي" = مجموع كل الفواتير بعد الخصم - كل المدفوعات =====
  async function recomputeBannerBalance(customerId) {
    if (!customerId) {
      setBannerBalance(0);
      return;
    }
    setBannerLoading(true);

    // 1) كل فواتير العميل (في كل المعارض وكل الوقت) + الخصم
    const { data: salesRows, error: salesErr } = await supabase
      .from("sale")
      .select("id, discount_value")
      .eq("customer_id", customerId);

    if (salesErr) {
      console.error("fetch sales for banner:", salesErr);
      setBannerLoading(false);
      return;
    }

    const saleIds = (salesRows || []).map((s) => s.id);
    let totalSalesNet = 0;

    if (saleIds.length) {
      // 2) بنود الفواتير
      const { data: detRows, error: detErr } = await supabase
        .from("sale_details")
        .select("sale_id, total_price")
        .in("sale_id", saleIds);

      if (detErr) {
        console.error("fetch sale_details for banner:", detErr);
        setBannerLoading(false);
        return;
      }

      const subtotalBySale = {};
      (detRows || []).forEach((d) => {
        subtotalBySale[d.sale_id] =
          (subtotalBySale[d.sale_id] || 0) + Number(d.total_price || 0);
      });

      for (const s of salesRows) {
        const sub = Number(subtotalBySale[s.id] || 0);
        const disc = Number(s.discount_value || 0); // ← اسم العمود عندك
        const net = Math.max(0, sub - disc);
        totalSalesNet += net;
      }
    }

    // 3) كل المدفوعات لكل الوقت
    const { data: payRows, error: payErr } = await supabase
      .from("payment")
      .select("amount")
      .eq("customer_id", customerId);

    if (payErr) {
      console.error("fetch payments for banner:", payErr);
      setBannerLoading(false);
      return;
    }

    const totalPaid = (payRows || []).reduce(
      (s, p) => s + Number(p.amount || 0),
      0
    );

    setBannerBalance(Number((totalSalesNet - totalPaid).toFixed(2)));
    setBannerLoading(false);
  }

  // ===== إنشاء دفعة =====
  async function handleSubmit(e) {
    e.preventDefault();
    if (savingPaymentRef.current) return;
    if (!form.customer_id || !form.amount || !form.payment_date) {
      alert("Complete all required fields!");
      return;
    }
    if (!showroomId) {
      alert("No showroom is assigned to this user.");
      return;
    }

    savingPaymentRef.current = true;
    setSavingPayment(true);
    try {
      const payload = {
        customer_id: Number(form.customer_id),
        amount: Number(form.amount),
        payment_date: new Date(form.payment_date).toISOString(),
        notes: form.notes || null,
        reference: form.reference || null,
        method: form.method || "cash",
        voucher_no: form.voucher_no || null,
        showroom_id: showroomId,
      };
      const { error } = await supabase.from("payment").insert([payload]);
      if (error) {
        console.error("Error inserting payment:", error);
        alert("Unable to save the payment: " + englishError(error.message));
        return;
      }

      // صفّي الفورم
      setForm({
        customer_id: "",
        amount: "",
        payment_date: todayISO(),
        notes: "",
        reference: "",
        method: "cash",
        voucher_no: "",
      });
      setShowAddPayment(false);

      // حدّث جدولك وحدّث شريط الرصيد
      await fetchOwnShowroomPayments();
      await recomputeBannerBalance(Number(payload.customer_id));

      alert("Payment saved ✅");
    } catch (err) {
      alert("Unable to save the payment: " + englishError(err.message));
    } finally {
      savingPaymentRef.current = false;
      setSavingPayment(false);
    }
  }

  // ===== بدء تعديل =====
  function startEdit(p) {
    setEditing(p);
    setEditForm({
      customer_id: String(p.customer_id || ""),
      amount: String(p.amount || ""),
      payment_date: p.payment_date ? String(p.payment_date).split("T")[0] : "",
      notes: p.notes || "",
      reference: p.reference || "",
      method: p.method || "cash",
      voucher_no: p.voucher_no || "",
    });
  }

  // ===== حفظ التعديل =====
  async function saveEdit(e) {
    e.preventDefault();
    if (!editing) return;
    if (!editForm.customer_id || !editForm.amount || !editForm.payment_date) {
      alert("Complete all required fields!");
      return;
    }
    const payload = {
      customer_id: Number(editForm.customer_id),
      amount: Number(editForm.amount),
      payment_date: new Date(editForm.payment_date).toISOString(),
      notes: editForm.notes || null,
      reference: editForm.reference || null,
      method: editForm.method || "cash",
      voucher_no: editForm.voucher_no || null,
      showroom_id: editing.showroom_id ?? showroomId,
    };
    const { error } = await supabase
      .from("payment")
      .update(payload)
      .eq("id", editing.id);
    if (error) {
      console.error("Error updating payment:", error);
      alert("Unable to update the payment: " + englishError(error.message));
      return;
    }
    const editedCustomerId = Number(payload.customer_id);

    setEditing(null);
    await fetchOwnShowroomPayments();
    if (showOthers) await fetchOthersShowrooms();
    await recomputeBannerBalance(editedCustomerId);

    alert("Payment updated ✅");
  }

  // ===== حذف =====
  async function deletePayment(id) {
    if (!window.confirm("Delete this payment?")) return;

    // هنجيب الدفعة الأول علشان نعرف عميلها، عشان نعيد حساب الرصيد بعد الحذف
    const { data: row } = await supabase
      .from("payment")
      .select("customer_id")
      .eq("id", id)
      .single();

    const { error } = await supabase.from("payment").delete().eq("id", id);
    if (error) {
      console.error("Error deleting payment:", error);
      alert("Unable to delete the payment: " + englishError(error.message));
      return;
    }

    await fetchOwnShowroomPayments();
    if (showOthers) await fetchOthersShowrooms();
    if (row?.customer_id) await recomputeBannerBalance(Number(row.customer_id));
  }

  // توزيع مبيعات العميل (للإحصاءات العامة داخل الجدول – غير مستخدم في شريط الرصيد)
  const salesPerCustomer = useMemo(() => {
    const m = {};
    for (const sd of salesDetails) {
      const cid = sd.sale?.customer_id;
      if (!cid) continue;
      m[cid] = (m[cid] || 0) + Number(sd.total_price || 0);
    }
    return m;
  }, [salesDetails]);

  const customerLookup = useMemo(
    () => Object.fromEntries(customers.map((c) => [c.id, c.name])),
    [customers]
  );


  // أزرار سريعة للفترة
  function setToday() {
    const t = todayISO();
    setDateFrom(t);
    setDateTo(t);
  }
  function setLast7() {
    const t = todayISO();
    setDateFrom(addDays(t, -6));
    setDateTo(t);
  }
  function setLast30() {
    const t = todayISO();
    setDateFrom(addDays(t, -29));
    setDateTo(t);
  }

  // toggle باقي المعارض
  async function toggleOthers() {
    const v = !showOthers;
    setShowOthers(v);
    if (v) {
      await fetchOthersShowrooms();
    } else {
      setOthersByShowroom({});
    }
  }

  return (
    <section className="payments-workspace" dir="ltr">
      <section className="payments-card payments-history">
        <div className="payments-section-heading">
          <div><h2>Payment history</h2><p className="payments-showroom">{showroomNameById[showroomId] || (showroomId ? `Showroom ${showroomId}` : "No assigned showroom")}</p></div>
          <div className="payments-header-actions">
          {isAdmin && <button type="button" className={showOthers ? "payments-primary" : "payments-secondary"} aria-pressed={showOthers} onClick={toggleOthers}>
            {showOthers ? "Hide other showrooms" : "Show other showrooms"}
          </button>}
          <button type="button" className="payments-primary" onClick={() => setShowAddPayment(true)} disabled={savingPayment}>Add payment</button>
          </div>
        </div>
        <div className="payments-filters">
          <label htmlFor="payments-from">From date<input id="payments-from" type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} /></label>
          <label htmlFor="payments-to">To date<input id="payments-to" type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} /></label>
          <div className="payments-date-presets" role="group" aria-label="Date range">
            <button onClick={setToday} type="button" className="payments-secondary">Today</button>
            <button onClick={setLast7} type="button" className="payments-secondary">Last 7 days</button>
            <button onClick={setLast30} type="button" className="payments-secondary">Last 30 days</button>
          </div>
        </div>
        <PaymentsTable payments={payments} customerLookup={customerLookup} onEdit={startEdit} onDelete={deletePayment} />
      </section>

      {isAdmin && showOthers && <section className="payments-other-showrooms">
        {loadingOthers && <p role="status">Loading other showrooms…</p>}
        {Object.keys(othersByShowroom).length === 0 && !loadingOthers && <div className="payments-card">No payments in other showrooms for this period.</div>}
        {Object.entries(othersByShowroom).map(([sid,list]) => <section className="payments-card" key={sid}>
          <div className="payments-section-heading"><div><h2>Payment history</h2><p className="payments-showroom">{showroomNameById[Number(sid)] || `Showroom ${sid}`}</p></div></div>
          <PaymentsTable payments={list} customerLookup={customerLookup} onEdit={startEdit} onDelete={deletePayment} />
        </section>)}
      </section>}

      {showAddPayment && <PaymentModal titleId="payment-add-title" onClose={() => { if (!savingPaymentRef.current) setShowAddPayment(false); }}>
        <div className="payments-section-heading"><h2 id="payment-add-title">Add payment</h2></div>
        {selectedCustomerId > 0 && <div className="payments-balance" role="status">
          <span>Customer balance · {selectedCustomer?.name}</span>
          <strong>{bannerLoading ? "Calculating…" : bannerBalance > 0
            ? `Balance due: ${bannerBalance.toLocaleString()} EGP`
            : bannerBalance < 0 ? `Credit: ${Math.abs(bannerBalance).toLocaleString()} EGP` : "Fully paid"}</strong>
        </div>}
        <form onSubmit={handleSubmit}><fieldset className="payments-modal-fields" disabled={savingPayment}>
          <PaymentFields form={form} setForm={setForm} customers={customers} idPrefix="payment-new" />
          <div className="payments-form-actions"><button type="button" className="payments-secondary" onClick={() => setShowAddPayment(false)}>Cancel</button><button type="submit" className="payments-primary">{savingPayment ? 'Saving…' : 'Save payment'}</button></div>
        </fieldset></form>
      </PaymentModal>}

      {editing && <PaymentModal titleId="payment-edit-title" onClose={() => setEditing(null)}>
          <div className="payments-section-heading"><h2 id="payment-edit-title">Edit payment #{editing.id}</h2></div>
          <form onSubmit={saveEdit}>
            <PaymentFields form={editForm} setForm={setEditForm} customers={customers} idPrefix="payment-edit" />
            <div className="payments-form-actions"><button type="button" className="payments-secondary" onClick={() => setEditing(null)}>Cancel</button><button type="submit" className="payments-primary">Save changes</button></div>
          </form>
      </PaymentModal>}
    </section>
  );
}

function PaymentModal({ titleId, onClose, children }) {
  const dialogRef = useRef(null);
  useEffect(() => {
    const dialog = dialogRef.current;
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    dialog.showModal();
    document.body.style.overflow = 'hidden';
    return () => {
      dialog.close();
      document.body.style.overflow = previousOverflow;
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, []);
  return <dialog ref={dialogRef} className="payments-modal" aria-labelledby={titleId}
    onCancel={event => { event.preventDefault(); onClose(); }}
    onClick={event => {
      if (event.target !== event.currentTarget) return;
      const bounds = event.currentTarget.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) onClose();
    }}>{children}</dialog>;
}

function PaymentFields({ form, setForm, customers, idPrefix }) {
  const update = (field,value) => setForm(current => ({ ...current, [field]: value }));
  return <div className="payments-fields">
    <div className="payments-field"><label htmlFor={`${idPrefix}-customer`}>Customer</label><select id={`${idPrefix}-customer`} required value={form.customer_id} onChange={e => update("customer_id",e.target.value)}>
      <option value="">Select a customer</option>{customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
    </select></div>
    <label htmlFor={`${idPrefix}-amount`}>Amount (EGP)<input id={`${idPrefix}-amount`} type="number" required min={1} value={form.amount} onChange={e => update("amount",e.target.value)} /></label>
    <label htmlFor={`${idPrefix}-date`}>Payment date<input id={`${idPrefix}-date`} type="date" required value={form.payment_date} onChange={e => update("payment_date",e.target.value)} /></label>
    <div className="payments-field"><label htmlFor={`${idPrefix}-method`}>Payment method</label><select id={`${idPrefix}-method`} value={form.method} onChange={e => update("method",e.target.value)}>
      {Object.entries(METHOD_LABELS).map(([value,label]) => <option key={value} value={value}>{label}</option>)}
    </select></div>
    <label htmlFor={`${idPrefix}-voucher`}>Voucher number<input id={`${idPrefix}-voucher`} value={form.voucher_no} onChange={e => update("voucher_no",e.target.value)} /></label>
    <label htmlFor={`${idPrefix}-reference`}>Reference<input id={`${idPrefix}-reference`} value={form.reference} onChange={e => update("reference",e.target.value)} /></label>
    <label className="payments-full-width" htmlFor={`${idPrefix}-notes`}>Notes<textarea id={`${idPrefix}-notes`} rows="2" value={form.notes} onChange={e => update("notes",e.target.value)} /></label>
  </div>;
}

function PaymentsTable({ payments, customerLookup, onEdit, onDelete }) {
  return <div className="payments-table-scroll"><table>
    <thead><tr>{["#","Customer","Amount (EGP)","Date","Payment method","Voucher number","Reference","Notes","Actions"].map(label => <th key={label}>{label}</th>)}</tr></thead>
    <tbody>
      {!payments.length && <tr><td colSpan={9} className="payments-empty">No payments in this period</td></tr>}
      {payments.map((p,i) => <tr key={p.id}>
        <td>{i+1}</td><td>{customerLookup[p.customer_id] || "-"}</td><td className="payments-number">{Number(p.amount).toLocaleString()}</td>
        <td>{p.payment_date ? String(p.payment_date).split("T")[0] : ""}</td><td>{METHOD_LABELS[p.method] || "-"}</td>
        <td>{p.voucher_no || "-"}</td><td>{p.reference || "-"}</td><td className="payments-notes">{p.notes || "-"}</td>
        <td><div className="payments-row-actions"><button type="button" className="payments-primary" onClick={() => onEdit?.(p)}>Edit</button><button type="button" className="payments-secondary" onClick={() => onDelete?.(p.id)}>Delete</button></div></td>
      </tr>)}
    </tbody>
  </table></div>;
}

export default PaymentTable;
